<template>
  <div class="s-setting-container no-border">
    <div class="main-header">
      <SaveCancelSection
        v-if="adminRole === 'admin'"
        :cancel="resetPartnerConfig"
        :save="updatePartnerConfig"
        :disabled="!changeCheck"
      />
    </div>
    <FilterPurchase
      :handler="handler"
      :strictFilterVendor="partnerSettings.STRICT_FILTER_VENDOR || null"
      :preferredFilterVendor="partnerSettings.PREFERRED_FILTER_VENDOR || null"
      :concomPartnerCode="partnerSettings.CONCOM_PARTNER_CODE || null"
      :method="method"
      :phone="phone"
      :website="website"
      :exception="exception"
      :onChange="handleChange"
      :errorObj="errorObj"
      :primaryColor="primaryColor"
      :companyName="companyName"
      :logoPreview="logoPreview"
    />
    <ProcessOverlay :show="updating" text="Saving" />
  </div>
</template>

<script>
import partnerSettingsMixin from '@shared/mixins/partnerSettingsMixin';

import FilterPurchase from '@shared/components/partnerSettings/FilterPurchase.vue';
import ProcessOverlay from '@shared/components/ProcessOverlay.vue';

import phoneValidation from '@shared/lib/errorCatch/phoneValidation';
import urlValidation from '@shared/lib/errorCatch/urlValidation';
import formatPhoneNumber from '@shared/lib/formatter/formatPhoneNumber';
import SaveCancelSection from '@shared/components/setting/shared/SaveCancelSection.vue';

export default {
  name: 'FilterPurchaseSection',
  components: {
    FilterPurchase,
    ProcessOverlay,
    SaveCancelSection,
  },
  mixins: [partnerSettingsMixin],
  props: {
    partnerSettings: Object,
    currentCorporation: Object,
    update: Function,
  },
  data() {
    const {
      FILTER_PURCHASE_HANDLER = 'smac',
      FILTER_PURCHASE_METHOD = '',
      FILTER_PURCHASE_WEBSITE = '',
      FILTER_PURCHASE_PHONE = '',
      FILTER_PURCHASE_EXCEPTION = '',
      LOGO_URL,
      PRIMARY_COLOR,
    } = this.partnerSettings;
    return {
      method: FILTER_PURCHASE_METHOD,
      phone: FILTER_PURCHASE_PHONE,
      website: FILTER_PURCHASE_WEBSITE,
      handler: FILTER_PURCHASE_HANDLER,
      exception: FILTER_PURCHASE_EXCEPTION || '',
      logoPreview: LOGO_URL,
      companyName: this.currentCorporation?.displayName || this.currentCorporation?.corporationName,
      primaryColor: PRIMARY_COLOR,
      errorObj: {},
      updating: false,
    };
  },
  computed: {
    changeCheck() {
      const {
        FILTER_PURCHASE_HANDLER = 'smac',
        FILTER_PURCHASE_METHOD = '',
        FILTER_PURCHASE_WEBSITE = '',
        FILTER_PURCHASE_PHONE = '',
        FILTER_PURCHASE_EXCEPTION = '',
      } = this.partnerSettings;
      let changes = false;
      if (FILTER_PURCHASE_METHOD !== this.method) {
        changes = true;
      }
      if (FILTER_PURCHASE_PHONE !== this.phone) {
        changes = true;
      }
      if (FILTER_PURCHASE_WEBSITE !== this.website) {
        changes = true;
      }
      if (FILTER_PURCHASE_HANDLER !== this.handler) {
        changes = true;
      }
      let currentException = this.exception;
      if (typeof FILTER_PURCHASE_EXCEPTION === 'string' && typeof this.exception !== 'string') {
        currentException = JSON.stringify(this.exception);
      }
      if (FILTER_PURCHASE_EXCEPTION !== currentException) {
        changes = true;
      }
      if (!changes) {
        this.changePartnerSettingsValue('showWarningOnExit', false);
      } else if (changes && !this.showWarningOnExit && !this.warningTab) {
        this.changePartnerSettingsValue('showWarningOnExit', true);
      }
      return changes;
    },
  },
  methods: {
    handleChange(field, value) {
      let v = value;
      if (field === 'phone') {
        v = formatPhoneNumber(v);
      }
      if (field === 'method') {
        this.handler = 'self';
      } else if (field === 'handler') {
        this.method = '';
      }
      this[field] = v;
    },
    resetPartnerConfig() {
      const {
        FILTER_PURCHASE_METHOD = 'smac',
        FILTER_PURCHASE_PHONE = '',
        FILTER_PURCHASE_WEBSITE = '',
        FILTER_PURCHASE_HANDLER = '',
        FILTER_PURCHASE_EXCEPTION = '',
      } = this.partnerSettings;
      this.method = FILTER_PURCHASE_METHOD;
      this.phone = FILTER_PURCHASE_PHONE;
      this.website = FILTER_PURCHASE_WEBSITE;
      this.handler = FILTER_PURCHASE_HANDLER;
      this.exception = FILTER_PURCHASE_EXCEPTION;
      this.errorObj = {};
    },
    async updatePartnerConfig() {
      const { method, phone, website, handler, exception } = this;
      const errorObj = {};
      if (handler === 'self') {
        if (!method) {
          errorObj.methodErr = 'Please select website or phone.';
        } else if (method === 'phone') {
          if (!phone) {
            errorObj.phoneErr = 'Phone number is required.';
          } else if (!phoneValidation(phone)) {
            errorObj.phoneErr = 'Phone number is not valid.';
          }
        } else if (method === 'website') {
          if (!website) {
            errorObj.websiteErr = 'Website is required.';
          } else if (!urlValidation(website)) {
            errorObj.websiteErr = 'Website URL is not valid.';
          }
        }
      } else {
        const errorInch = [];
        if (!exception.merv8.oneInch && !exception.merv11.oneInch && !exception.merv13.oneInch) {
          errorInch.push('1"');
        }
        if (!exception.merv8.twoInch && !exception.merv11.twoInch && !exception.merv13.twoInch) {
          errorInch.push('2"');
        }
        if (
          !exception.merv8.threeInch &&
          !exception.merv11.threeInch &&
          !exception.merv13.threeInch
        ) {
          errorInch.push('3"');
        }
        if (!exception.merv8.fourInch && !exception.merv11.fourInch && !exception.merv13.fourInch) {
          errorInch.push('4"');
        }
        if (!exception.merv8.fiveInch && !exception.merv11.fiveInch && !exception.merv13.fiveInch) {
          errorInch.push('5"');
        }
        if (!exception.merv8.sixInch && !exception.merv11.sixInch && !exception.merv13.sixInch) {
          errorInch.push('6"');
        }
        if (errorInch.length > 0) {
          errorObj.mervErr = `Please select at least one merv for ${errorInch.join(', ')}`;
        }
      }
      this.errorObj = errorObj;
      if (Object.keys(errorObj).length === 0) {
        this.changePartnerSettingsValue('showWarningOnExit', false);
        this.updating = true;
        await this.update({
          FILTER_PURCHASE_METHOD: method,
          FILTER_PURCHASE_PHONE: phone,
          FILTER_PURCHASE_WEBSITE: website,
          FILTER_PURCHASE_HANDLER: handler,
          FILTER_PURCHASE_EXCEPTION:
            typeof exception === 'string' ? exception : JSON.stringify(exception),
        });
        this.updating = false;
      }
    },
  },
};
</script>
